import React, { useState, useEffect } from "react";
import { apiRequestWithAuth } from "../../utils/api";
import Alert from "../../components/ui/alert/Alert";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import { useNavigate } from "react-router-dom";
import Select from "react-tailwindcss-select";
import { SelectValue } from "react-tailwindcss-select/dist/components/type";

type Course = {
    id: number;
    title: string;
};

type CourseOption = {
    value: string;
    label: string;
};

type DynamicBlockFormData = {
    name: string;
    shortDescription: string;
    status: number;
    orderNo: number;
    courseIds: number[];
};

const CreateDynamicBlock = () => {
    const defaultForm: DynamicBlockFormData = {
        name: "",
        shortDescription: "",
        status: 1,
        orderNo: 0,
        courseIds: [],
    };

    const [formData, setFormData] = useState<DynamicBlockFormData>(defaultForm);
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourses, setSelectedCourses] = useState<SelectValue>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();

    // Fetch courses with search
    const fetchCourses = async (search: string = "") => {
        try {
            const endpoint = search
                ? `/course?status=1&search=${encodeURIComponent(search)}`
                : "/course?status=1";
            const res = await apiRequestWithAuth(endpoint, "GET", {});
            if (res.messageCode === 200) {
                setCourses(res.result);
            }
        } catch (error) {
            console.error("Failed to load courses:", error);
        }
    };

    // Initial load courses
    useEffect(() => {
        fetchCourses();
    }, []);

    // Search courses when searchTerm changes
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchTerm) {
                fetchCourses(searchTerm);
            } else {
                fetchCourses();
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === "status" || name === "orderNo"
                ? parseInt(value) || 0
                : value
        }));
    };

    const handleCourseChange = (value: SelectValue) => {
        setSelectedCourses(value);

        // Convert selected values to courseIds array
        const courseIds = Array.isArray(value)
            ? value.map((item: any) => parseInt(item.value))
            : [];

        setFormData(prev => ({
            ...prev,
            courseIds: courseIds
        }));
    };

    const handleRemoveCourse = (courseId: number) => {
        const newSelected = Array.isArray(selectedCourses)
            ? selectedCourses.filter((item: any) => parseInt(item.value) !== courseId)
            : [];

        setSelectedCourses(newSelected);
        setFormData(prev => ({
            ...prev,
            courseIds: prev.courseIds.filter(id => id !== courseId)
        }));
    };


    const handleSave = async () => {
        if (!formData.name) {
            alert("Vui lòng nhập đủ thông tin bắt buộc (Tên block)");
            return;
        }

        setIsLoading(true);
        try {
            const res = await apiRequestWithAuth("/dynamic-blocks", "POST", formData);

            if (res.messageCode === 200) {
                setOpenSnackbar(true);
                setFormData(defaultForm);
                setSelectedCourses([]);
                setTimeout(() => navigate("/dynamic-blocks"), 1000);
            } else {
                setErrorMessage(res.message || "Tạo mới thất bại. Vui lòng thử lại.");
                setErrorSnackbar(true);
                setTimeout(() => setErrorSnackbar(false), 3000);
            }
        } catch (error) {
            console.error("Failed to create dynamic block:", error);
            setErrorMessage("Tạo mới thất bại. Vui lòng thử lại.");
            setErrorSnackbar(true);
            setTimeout(() => setErrorSnackbar(false), 3000);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            {openSnackbar && (
                <Alert
                    variant="success"
                    title="Tạo Dynamic Block thành công"
                    message="Bạn đã tạo Dynamic Block mới thành công."
                />
            )}

            {errorSnackbar && (
                <Alert
                    variant="error"
                    title="Lỗi"
                    message={errorMessage}
                />
            )}

            <PageMeta title="Tạo Dynamic Block" description="Trang tạo mới Dynamic Block" />
            <PageBreadcrumb pageTitle="Tạo Dynamic Block" />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
                <div className="space-y-6">
                    <ComponentCard title="Thông tin Dynamic Block">
                        <div className="space-y-6">
                            <div>
                                <Label>Tên block <span className="text-red-500">*</span></Label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Nhập tên block"
                                />
                            </div>

                            <div>
                                <Label>Mô tả ngắn</Label>
                                <Input
                                    name="shortDescription"
                                    value={formData.shortDescription}
                                    onChange={handleChange}
                                    placeholder="Nhập mô tả ngắn"
                                />
                            </div>

                            <div className="ctmSelectMulti">
                                <Label>Khóa học</Label>
                                <Select
                                    value={selectedCourses}
                                    onChange={handleCourseChange}
                                    options={courses.map(course => ({
                                        value: course.id.toString(),
                                        label: course.title
                                    }))}
                                    primaryColor="blue"
                                    isMultiple={true}
                                    isSearchable={true}
                                    placeholder="Chọn khóa học..."
                                    searchInputPlaceholder="Tìm kiếm khóa học..."
                                    formatOptionLabel={(data: any) => (
                                        <li
                                            className={`block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded hover:bg-blue-100`}
                                        >
                                            {data.label}
                                        </li>
                                    )}
                                    classNames={{
                                        menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-800",
                                        list: "dark:bg-gray-800 !max-h-72 overflow-y-auto"
                                    }}
                                />

                                {/* Selected Courses List */}
                                {Array.isArray(selectedCourses) && selectedCourses.length > 0 && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Các khóa học đã chọn:
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedCourses.map((course: any) => (
                                                <div
                                                    key={course.value}
                                                    className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5"
                                                >
                                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                                        {course.label}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveCourse(parseInt(course.value))}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                        title="Xóa khóa học"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label>Thứ tự</Label>
                                <Input
                                    name="orderNo"
                                    type="number"
                                    value={formData.orderNo}
                                    onChange={handleChange}
                                    placeholder="0"
                                />
                            </div>

                            <div>
                                <Label>Trạng thái</Label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                >
                                    <option value="1">Active</option>
                                    <option value="2">Draft</option>
                                    <option value="3">Inactive</option>
                                    <option value="4">Delete</option>
                                </select>
                            </div>
                        </div>
                    </ComponentCard>

                    <ComponentCard title="">
                        <div className="flex gap-4">
                            <button
                                onClick={handleSave}
                                disabled={isLoading}
                                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? "Đang tạo..." : "Tạo mới"}
                            </button>
                            <button
                                onClick={() => navigate("/dynamic-blocks")}
                                className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400 transition"
                            >
                                Hủy
                            </button>
                        </div>
                    </ComponentCard>
                </div>
            </div>
        </div>
    );
};

export default CreateDynamicBlock;

